import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Skeleton,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  MenuBook as KnowledgeIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  PowerSettingsNew as PowerIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants';
import { ragService } from '../services/ragService';

const KnowledgeBases = () => {
  const navigate = useNavigate();

  // Estados para dados e loading
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [filteredKBs, setFilteredKBs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para filtros e busca
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0); // 0: Todos, 1: Ativos, 2: Inativas
  
  // Estados para ações
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedKB, setSelectedKB] = useState(null);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [affectedAgents, setAffectedAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Carregar bases de conhecimento (usar summary)
  const loadKnowledgeBases = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Buscar ativas por padrão para a aba inicial (Todos = cache local filtra)
      const data = await ragService.getKnowledgeBasesSummary(null);
      const items = (data.knowledge_bases || []).map(kb => ({
        id: kb.id,
        name: kb.name,
        description: kb.description,
        is_active: kb.is_active,
        tool_name: kb.tool_name,
      }));
      setKnowledgeBases(items);
    } catch (err) {
      console.error('Erro ao carregar bases de conhecimento:', err);
      setError('Erro ao carregar bases de conhecimento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKnowledgeBases();
  }, []);

  // Filtrar KBs baseado na busca e tab
  useEffect(() => {
    let filtered = knowledgeBases;

    // Filtrar por termo de busca
    if (searchTerm) {
      filtered = filtered.filter(kb =>
        kb.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        kb.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por tab
    switch (currentTab) {
      case 1: // Ativos
        filtered = filtered.filter(kb => kb.is_active === true);
        break;
      case 2: // Inativas
        filtered = filtered.filter(kb => kb.is_active === false);
        break;
      default: // Todos
        break;
    }

    setFilteredKBs(filtered);
  }, [knowledgeBases, searchTerm, currentTab]);

  // Handlers para ações
  const handleCreateKB = () => {
    navigate(ROUTES.KNOWLEDGE_BASE_CREATE);
  };

  const handleViewKB = (kb) => {
    navigate(ROUTES.KNOWLEDGE_BASE_DETAIL.replace(':kbId', kb.id));
  };

  const handleEditKB = (kb) => {
    navigate(ROUTES.KNOWLEDGE_BASE_EDIT.replace(':kbId', kb.id));
  };

  const handleToggleStatus = async (kb) => {
    setSelectedKB(kb);
    
    // Se a KB está ativa, buscar agentes afetados
    if (kb.is_active) {
      setLoadingAgents(true);
      try {
        const agents = await ragService.getAgentsUsingKnowledgeBase(kb.id);
        setAffectedAgents(agents);
      } catch (err) {
        console.error('Erro ao buscar agentes afetados:', err);
        setAffectedAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    } else {
      setAffectedAgents([]);
    }
    
    setToggleDialogOpen(true);
  };

  const handleToggleConfirm = async () => {
    if (!selectedKB) return;

    try {
      setToggling(true);
      
      if (selectedKB.is_active) {
        // Desativar base de conhecimento
        await ragService.deleteKnowledgeBase(selectedKB.id);
        
        setSnackbar({
          open: true,
          message: `Base de conhecimento '${selectedKB.name}' desativada com sucesso!`,
          severity: 'success'
        });
      } else {
        // Reativar base de conhecimento
        await ragService.reactivateKnowledgeBase(selectedKB.id);
        
        setSnackbar({
          open: true,
          message: `Base de conhecimento '${selectedKB.name}' ativada! Agora pode ser habilitada manualmente em cada agente.`,
          severity: 'success'
        });
      }
      
      // Recarregar lista
      loadKnowledgeBases();
    } catch (err) {
      console.error('Erro ao alterar status da base de conhecimento:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao alterar status da base de conhecimento. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setToggling(false);
      setToggleDialogOpen(false);
      setSelectedKB(null);
      setAffectedAgents([]);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Componente para card de KB
  const KnowledgeBaseCard = ({ kb }) => (
    <Card
      component={motion.div}
      whileHover={{ scale: 1.02 }}
      sx={{
        height: '180px', // Altura aumentada para melhor espaçamento
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
        },
        transition: 'all 0.3s ease',
        // Aplicar degradê vermelho para bases inativas
        ...(kb.is_active ? {} : {
          background: 'linear-gradient(145deg, #ffffff 0%, rgba(244, 67, 54, 0.03) 100%)',
          border: '1px solid rgba(244, 67, 54, 0.1)',
          boxShadow: '0 1px 5px rgba(244, 67, 54, 0.08)',
        }),
      }}
      onClick={() => handleViewKB(kb)}
    >
      <CardContent sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header compacto com ícone e nome */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <KnowledgeIcon 
              color={kb.is_active ? "success" : "error"} 
              sx={{ 
                fontSize: 16,
                ...(kb.is_active ? {} : {
                  filter: 'drop-shadow(0 2px 4px rgba(244, 67, 54, 0.2))',
                })
              }}
            />
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: 1.2,
                color: kb.is_active ? 'text.primary' : 'error.dark'
              }}
            >
              {kb.name}
            </Typography>
          </Box>
          
          {/* Chips de status compactos */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={kb.is_active ? 'Ativa' : 'Inativa'}
              color={kb.is_active ? 'success' : 'error'}
              size="small"
              sx={{ 
                fontSize: '0.65rem',
                height: 18,
                fontWeight: 500,
                ...(kb.is_active ? {} : {
                  background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
                  color: 'white',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(244, 67, 54, 0.2)',
                })
              }}
            />
            {kb.retrieval_method && (
              <Chip
                label={kb.retrieval_method === 'hybrid' ? 'Híbrido' : 'Semântico'}
                size="small"
                variant="outlined"
                sx={{ 
                  fontSize: '0.65rem',
                  height: 18,
                  fontWeight: 500,
                  ...(kb.is_active ? {} : {
                    borderColor: 'error.light',
                    color: 'error.dark',
                  })
                }}
              />
            )}
          </Box>
        </Box>

        {/* Descrição compacta com tooltip */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography 
            variant="body2" 
            color={kb.is_active ? "text.secondary" : "error.main"}
            sx={{ 
              fontSize: '0.75rem',
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              opacity: kb.is_active ? 1 : 0.8,
              flexGrow: 1,
              mr: 1,
            }}
          >
            {kb.description || `Essa base de conhecimento contém informações sobre ${kb.name.toLowerCase()}`}
          </Typography>

          {/* Ícone de info com tooltip */}
          <Tooltip 
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {kb.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {kb.description || `Essa base de conhecimento contém informações sobre ${kb.name.toLowerCase()}`}
                </Typography>
                <Typography variant="caption">
                  Status: {kb.is_active ? 'Ativa' : 'Inativa'}<br/>
                  Método: {kb.retrieval_method === 'hybrid' ? 'Híbrido' : 'Semântico'}<br/>
                  Documentos: {kb.total_documents || 0}<br/>
                  Chunks: {kb.total_chunks || 0}
                </Typography>
              </Box>
            }
            placement="top"
          >
            <IconButton 
              size="small"
              sx={{ 
                color: kb.is_active ? 'text.disabled' : 'error.light',
                p: 0.5,
              }}
            >
              <InfoIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Estatísticas compactas */}
        <Box sx={{ mb: 1 }}>
          {/* Removido: docs/chunks para otimizar listagem */}
        </Box>
        
        {/* Ações compactas */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 'auto' }}>
          <Tooltip title="Editar base de conhecimento">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleEditKB(kb);
              }}
              color="primary"
              size="small"
              sx={{
                p: 0.5,
                ...(kb.is_active ? {} : {
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  }
                })
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={kb.is_active ? 'Desativar base de conhecimento' : 'Ativar base de conhecimento'}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleToggleStatus(kb);
              }}
              color={kb.is_active ? 'warning' : 'success'}
              size="small"
              sx={{
                p: 0.5,
                ...(kb.is_active ? {} : {
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  }
                })
              }}
            >
              <PowerIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  // Loading skeleton
  const LoadingSkeleton = () => (
    <Box 
      sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: 3,
      }}
    >
      {[...Array(8)].map((_, index) => (
        <Card key={index} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
              <Skeleton variant="rectangular" width={120} height={20} sx={{ borderRadius: '10px' }} />
              <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '10px' }} />
            </Box>
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="50%" height={20} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Skeleton variant="text" width={80} height={16} />
              <Skeleton variant="text" width={80} height={16} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <Box sx={{ p: 4 }}>
      {/* Filtros e Busca */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="standard">
            <Tab label={`Todos (${knowledgeBases.length})`} />
            <Tab label={`Ativos (${knowledgeBases.filter(kb => kb.is_active === true).length})`} />
            <Tab label={`Inativas (${knowledgeBases.filter(kb => kb.is_active === false).length})`} />
          </Tabs>
          
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleCreateKB}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.dark',
              },
              borderRadius: 1,
              px: 2,
              py: 0.5,
            }}
          >
            Criar Base de Conhecimento
          </Button>
        </Box>

        <TextField
          size="small"
          placeholder="Buscar bases de conhecimento..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
      </Box>

      {/* Conteúdo */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          <AnimatePresence>
            {filteredKBs.map((kb, index) => (
              <motion.div
                key={kb.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <KnowledgeBaseCard kb={kb} />
              </motion.div>
            ))}
          </AnimatePresence>
        </Box>
      )}

      {/* Empty State */}
      {!loading && filteredKBs.length === 0 && !error && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <KnowledgeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'Nenhuma base encontrada' : 
             currentTab === 1 ? 'Nenhuma base ativa encontrada' :
             currentTab === 2 ? 'Nenhuma base inativa encontrada' :
             'Nenhuma base de conhecimento criada'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm 
              ? 'Tente ajustar os filtros de busca'
              : currentTab === 1 
                ? 'Todas as bases de conhecimento estão inativas'
                : currentTab === 2
                  ? 'Todas as bases de conhecimento estão ativas'
                  : 'Crie sua primeira base de conhecimento para começar'
            }
          </Typography>
          {!searchTerm && currentTab === 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateKB}>
              Criar Primeira Base
            </Button>
          )}
        </Box>
      )}

      {/* Dialog de confirmação de ativação/desativação */}
      <Dialog open={toggleDialogOpen} onClose={() => setToggleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedKB?.is_active ? 'Desativar Base de Conhecimento' : 'Ativar Base de Conhecimento'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja {selectedKB?.is_active ? 'desativar' : 'ativar'} a base de conhecimento "{selectedKB?.name}"?
            <br /><br />
            <strong>Esta ação irá:</strong>
            <br />
            {"• " + (selectedKB?.is_active ? 'Desativar' : 'Ativar') + " a base de conhecimento"}
            {selectedKB?.is_active && (
              <>
                <br />
                {"• Remover automaticamente de todos os agentes que a utilizam"}
                {loadingAgents ? (
                  <><br />{"• Carregando agentes afetados..."}</>
                ) : affectedAgents.length > 0 ? (
                  <>
                    <br /><br />
                    <strong>Agentes que serão afetados ({affectedAgents.length}):</strong>
                  </>
                ) : (
                  <><br />{"• Nenhum agente está utilizando esta base atualmente"}</>
                )}
              </>
            )}
            {!selectedKB?.is_active && (
              <>
                <br />
                {"• A base ficará disponível para ser adicionada aos agentes (deve ser habilitada manualmente em cada agente)"}
              </>
            )}
          </DialogContentText>
          
          {selectedKB?.is_active && affectedAgents.length > 0 && !loadingAgents && (
            <List dense sx={{ mt: 2, border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
              {affectedAgents.map((agent) => (
                <ListItem key={agent.id}>
                  <ListItemText 
                    primary={agent.name}
                    secondary={agent.description}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleDialogOpen(false)} disabled={toggling}>
            Cancelar
          </Button>
          <Button 
            onClick={handleToggleConfirm} 
            color={selectedKB?.is_active ? 'warning' : 'success'}
            variant="contained"
            disabled={toggling || loadingAgents}
          >
            {toggling ? (selectedKB?.is_active ? 'Desativando...' : 'Ativando...') : (selectedKB?.is_active ? 'Desativar' : 'Ativar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KnowledgeBases; 